import { useState } from 'react';
import { Card, Title, Badge, Text, Textarea, Flex, Divider } from '@tremor/react';
import { api } from '../api';
import { useApi } from './useApi';
import { SURVEY_OPTIONS } from './Filters';

type InboxRow = {
  response_id: string;
  recipient_id: string;
  member_id: string;
  survey_key: string;
  dock_name: string | null;
  first_name: string | null;
  last_name: string | null;
  completed_at: string;
  nps_score: number | null;
  sentiment: string | null;
  flag_reason: string | null;
  draft_id: string | null;
  draft_text: string | null;
  final_text: string | null;
  draft_status: string | null;
  draft_updated_at: string | null;
};

type InboxPayload = { total: number; limit: number; offset: number; rows: InboxRow[] };

const STATUSES: Array<{ value: string; label: string }> = [
  { value: '',                label: 'Open (pending + edited)' },
  { value: 'pending_review',  label: 'Pending review' },
  { value: 'edited',          label: 'Edited' },
  { value: 'approved',        label: 'Approved' },
  { value: 'sent',            label: 'Sent' },
  { value: 'discarded',       label: 'Discarded' },
];

export function InboxPage(): JSX.Element {
  const [status, setStatus] = useState('');
  const [surveyKey, setSurveyKey] = useState('');
  const [bump, setBump] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);

  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (surveyKey) params.set('survey_key', surveyKey);
  const data = useApi<InboxPayload>(`/api/admin/reviewer/inbox?${params.toString()}`, [status, surveyKey, bump]);

  const refresh = (): void => setBump((b) => b + 1);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <Title>Reviewer inbox</Title>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4 p-4 bg-white border border-gray-200 rounded-lg">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500 uppercase tracking-wide">Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm">
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500 uppercase tracking-wide">Survey</span>
          <select value={surveyKey} onChange={(e) => setSurveyKey(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm">
            {SURVEY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Card className="lg:col-span-2">
          {data.kind === 'loading' && <Text>Loading…</Text>}
          {data.kind === 'error' && <Text>Couldn't load inbox ({data.message})</Text>}
          {data.kind === 'ready' && (
            <div className="space-y-2">
              <Text className="text-xs text-gray-500">{data.data.total} flagged</Text>
              {data.data.rows.map((row) => (
                <button
                  type="button"
                  key={row.response_id}
                  onClick={() => setSelected(row.response_id)}
                  className={`w-full text-left p-3 border rounded ${selected === row.response_id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">
                      {[row.first_name, row.last_name].filter(Boolean).join(' ') || '(unknown)'}
                    </span>
                    <Badge color="red">{row.flag_reason ?? 'flag'}</Badge>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
                    <span>{surveyLabel(row.survey_key)} · NPS {row.nps_score ?? '—'} · {row.dock_name ?? '—'}</span>
                    <span>{formatRelative(row.completed_at)}</span>
                  </div>
                  {row.draft_status && (
                    <div className="text-xs mt-1">
                      <Badge color={draftStatusColor(row.draft_status)}>{row.draft_status}</Badge>
                    </div>
                  )}
                </button>
              ))}
              {data.data.rows.length === 0 && <Text>No flagged responses match these filters.</Text>}
            </div>
          )}
        </Card>

        <Card className="lg:col-span-3">
          {selected
            ? <InboxDetail responseId={selected} onChanged={refresh} />
            : <Text>Pick a response on the left to review.</Text>}
        </Card>
      </div>
    </div>
  );
}

type Detail = {
  response: {
    response_id: string; survey_key: string; first_name: string | null; last_name: string | null;
    dock_name: string | null; nps_score: number | null; sentiment: string | null; flag_reason: string | null;
    completed_at: string | null;
  };
  answers: Array<{ question_key: string; value_json: unknown; value_text: string | null }>;
};

function InboxDetail(props: { responseId: string; onChanged: () => void }): JSX.Element {
  const { responseId, onChanged } = props;
  const [bump, setBump] = useState(0);
  const detail = useApi<Detail>(`/api/admin/metrics/responses/${responseId}`, [responseId, bump]);
  // Inbox row carries draft state, but we re-fetch for the latest text.
  const inbox = useApi<InboxPayload>(`/api/admin/reviewer/inbox`, [bump]);
  const inboxRow = inbox.kind === 'ready' ? inbox.data.rows.find((r) => r.response_id === responseId) : undefined;

  const [editing, setEditing] = useState<string | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [discardReason, setDiscardReason] = useState('');

  if (detail.kind === 'loading') return <Text>Loading…</Text>;
  if (detail.kind === 'error') return <Text>Couldn't load response ({detail.message})</Text>;

  const draftDisplay = editing ?? inboxRow?.final_text ?? inboxRow?.draft_text ?? '';
  const draftId = inboxRow?.draft_id;

  const onSave = async (): Promise<void> => {
    if (!draftId || editing === null) return;
    await api(`/api/admin/reviewer/drafts/${draftId}`, {
      method: 'PATCH',
      body: JSON.stringify({ final_text: editing }),
    });
    setEditing(null);
    setBump((b) => b + 1);
    onChanged();
  };
  const onApprove = async (): Promise<void> => {
    if (!draftId) return;
    await api(`/api/admin/reviewer/drafts/${draftId}/approve`, { method: 'POST' });
    onChanged();
  };
  const onDiscard = async (): Promise<void> => {
    if (!draftId || !discardReason.trim()) return;
    await api(`/api/admin/reviewer/drafts/${draftId}/discard`, {
      method: 'POST',
      body: JSON.stringify({ reason: discardReason.trim() }),
    });
    setDiscardOpen(false); setDiscardReason('');
    onChanged();
  };

  return (
    <div>
      <Flex alignItems="center" justifyContent="between" className="mb-2">
        <Title>{[detail.data.response.first_name, detail.data.response.last_name].filter(Boolean).join(' ') || '(unknown)'}</Title>
        <Badge color="red">{detail.data.response.flag_reason ?? 'flagged'}</Badge>
      </Flex>
      <Text className="text-xs text-gray-500">
        {surveyLabel(detail.data.response.survey_key)} · NPS {detail.data.response.nps_score ?? '—'} · {detail.data.response.dock_name ?? '—'} · {formatDate(detail.data.response.completed_at)}
      </Text>

      <Divider />

      <div className="space-y-3 mb-6">
        {detail.data.answers.map((a) => (
          <div key={a.question_key}>
            <div className="text-xs uppercase text-gray-500 tracking-wide">{a.question_key}</div>
            <div className="mt-1 text-sm">{formatAnswer(a.value_json, a.value_text)}</div>
          </div>
        ))}
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs uppercase text-gray-500 tracking-wide">AI draft</span>
          {inboxRow?.draft_status && <Badge color={draftStatusColor(inboxRow.draft_status)}>{inboxRow.draft_status}</Badge>}
        </div>
        {draftId
          ? (
            <>
              {editing === null
                ? <pre className="whitespace-pre-wrap text-sm">{draftDisplay || '(empty)'}</pre>
                : <Textarea value={editing} onChange={(e) => setEditing(e.target.value)} rows={8} />}
              <div className="flex items-center gap-2 mt-3">
                {editing === null
                  ? <button type="button" onClick={() => setEditing(draftDisplay)} className="text-sm border border-gray-300 rounded px-3 py-1.5 hover:bg-white">Edit</button>
                  : (
                    <>
                      <button type="button" onClick={() => void onSave()} className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded px-3 py-1.5">Save</button>
                      <button type="button" onClick={() => setEditing(null)} className="text-sm border border-gray-300 rounded px-3 py-1.5 hover:bg-white">Cancel</button>
                    </>
                  )}
                <button type="button" onClick={() => void onApprove()} className="text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded px-3 py-1.5 ml-auto">Approve & send</button>
                <button type="button" onClick={() => setDiscardOpen(true)} className="text-sm border border-red-300 text-red-600 rounded px-3 py-1.5 hover:bg-red-50">Discard</button>
              </div>
            </>
          )
          : (
            <Text>No AI draft yet — Phase 6 wires drafting to flagged responses.</Text>
          )
        }

        {discardOpen && (
          <div className="mt-3 p-3 border border-red-200 rounded bg-red-50">
            <div className="text-xs uppercase text-red-600 tracking-wide mb-2">Discard reason (required)</div>
            <Textarea value={discardReason} onChange={(e) => setDiscardReason(e.target.value)} rows={2} />
            <div className="flex gap-2 mt-2">
              <button type="button" onClick={() => void onDiscard()} disabled={!discardReason.trim()} className="text-sm bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded px-3 py-1.5">Confirm discard</button>
              <button type="button" onClick={() => { setDiscardOpen(false); setDiscardReason(''); }} className="text-sm border border-gray-300 rounded px-3 py-1.5">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(diff / 3600 / 1000);
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
function surveyLabel(key: string): string {
  return ({ post_sale: 'Post-sale', post_trip: 'Post-trip', post_training: 'Post-training' } as Record<string, string>)[key] ?? key;
}
function formatAnswer(json: unknown, text: string | null): string {
  if (text) return text;
  if (json === null || json === undefined) return '—';
  if (typeof json === 'string') return json;
  if (typeof json === 'number' || typeof json === 'boolean') return String(json);
  if (Array.isArray(json)) return json.map((x) => (typeof x === 'string' ? x : JSON.stringify(x))).join(', ');
  return JSON.stringify(json);
}
function draftStatusColor(s: string): 'gray' | 'blue' | 'emerald' | 'amber' | 'red' {
  switch (s) {
    case 'pending_review': return 'gray';
    case 'edited': return 'amber';
    case 'approved': return 'emerald';
    case 'sent': return 'emerald';
    case 'discarded': return 'red';
    default: return 'gray';
  }
}
